import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Send, ShieldCheck } from 'lucide-react';
import { formatShortDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';

interface PendingDisbursement {
  loanId: string;
  loanNumber: string;
  applicationNumber: string;
  approvedAt: string | null;
  memberName: string;
  membershipNumber: string;
  productName: string;
  approvedAmount: string;
  approvedAmountFormatted: string;
  installmentCount: number;
  bankAccount: {
    bankName: string;
    accountNumber: string;
    accountName: string;
    verified: boolean;
  } | null;
  guarantors: Array<{ name: string; membershipNumber: string }>;
}

export default function AdminDisbursements() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<PendingDisbursement | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['pending-disbursements'],
    queryFn: () => api.get<PendingDisbursement[]>('/disbursements/pending'),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="Disbursements"
        description="Approved loans awaiting payment to the member."
      />

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : data.length === 0 ? (
        <EmptyState
          icon={<Send size={20} />}
          title="Nothing awaiting disbursement"
          description="Approved loans appear here for finance to pay out."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {data.map((item) => (
            <Card key={item.loanId}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="numeric">{item.loanNumber}</CardTitle>
                    <p className="mt-1 text-sm text-foreground-secondary">
                      {item.memberName} · {item.membershipNumber}
                    </p>
                  </div>
                  <p className="numeric font-heading text-xl font-black text-foreground">
                    {item.approvedAmountFormatted}
                  </p>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Detail label="Product" value={item.productName} />
                  <Detail label="Repayments" value={`${item.installmentCount} monthly`} />
                  <Detail
                    label="Approved"
                    value={item.approvedAt ? formatShortDate(new Date(item.approvedAt)) : '—'}
                  />
                  <Detail label="Application" value={item.applicationNumber} />
                </div>

                {item.bankAccount ? (
                  <div className="rounded-lg border border-border bg-muted p-3">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-foreground-secondary">
                      Destination account
                    </p>
                    <p className="numeric text-sm font-semibold text-foreground">
                      {item.bankAccount.accountNumber} · {item.bankAccount.bankName}
                    </p>
                    <p className="text-xs text-foreground-secondary">
                      {item.bankAccount.accountName}
                      {!item.bankAccount.verified && ' · not verified'}
                    </p>
                  </div>
                ) : (
                  <Alert tone="warning">
                    <span className="inline-flex items-center gap-1.5">
                      <AlertTriangle size={14} /> No bank account on file. The member must add one
                      before this can be paid.
                    </span>
                  </Alert>
                )}

                <div className="flex items-start gap-2 text-xs text-foreground-secondary">
                  <ShieldCheck size={14} className="mt-0.5 shrink-0 text-success" />
                  <span>
                    Guaranteed by {item.guarantors.map((g) => g.name).join(', ') || '—'}
                  </span>
                </div>

                <Button full disabled={!item.bankAccount} onClick={() => setSelected(item)}>
                  <Send size={16} /> Record disbursement
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selected && (
        <DisbursePanel
          item={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            void queryClient.invalidateQueries({ queryKey: ['pending-disbursements'] });
            void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
          }}
        />
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-foreground-secondary">{label}</p>
      <p className="numeric text-foreground">{value}</p>
    </div>
  );
}

function DisbursePanel({
  item,
  onClose,
  onDone,
}: {
  item: PendingDisbursement;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState((Number(item.approvedAmount) / 100).toFixed(2));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const short = Number(amount) * 100 < Number(item.approvedAmount);

  const disburse = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/disbursements', {
        loanId: item.loanId,
        disbursedAmount: amount,
        disbursementDate: date,
        paymentMethod: method,
        transactionReference: reference,
        ...(item.bankAccount
          ? {
              bankName: item.bankAccount.bankName,
              accountNumber: item.bankAccount.accountNumber,
              accountName: item.bankAccount.accountName,
            }
          : {}),
        ...(notes ? { notes } : {}),
      }),
    onSuccess: (result) => {
      toast.success(result.message);
      onDone();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/50 p-0 sm:items-center sm:p-6">
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <CardHeader>
          <CardTitle>Disburse {item.loanNumber}</CardTitle>
          <p className="text-sm text-foreground-secondary">
            {item.memberName} · approved {item.approvedAmountFormatted}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          <Field label="Amount disbursed (₦)" htmlFor="amount" required>
            <Input
              id="amount"
              className="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            />
          </Field>

          {short && (
            <Alert tone="info">
              You are disbursing less than the approved amount. The repayment schedule will be
              rebuilt so the member repays exactly what they received — never more.
            </Alert>
          )}

          <Field label="Disbursement date" htmlFor="date" required>
            <Input
              id="date"
              type="date"
              max={new Date().toISOString().slice(0, 10)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>

          <Field label="Payment method" htmlFor="method" required>
            <Select id="method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {['BANK_TRANSFER', 'CASH', 'CHEQUE', 'ONLINE', 'OTHER'].map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Transaction reference"
            htmlFor="reference"
            required
            hint="Must be unique. This is the duplicate-payment guard."
          >
            <Input id="reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>

          <Field label="Notes" htmlFor="notes">
            <Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          <div className="flex gap-3 border-t border-border pt-4">
            <Button
              full
              loading={disburse.isPending}
              disabled={!amount || !reference.trim()}
              onClick={() => disburse.mutate()}
            >
              Confirm disbursement
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
